import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, AlertTriangle } from 'lucide-react';

const DeleteAccountPage = () => {
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth?redirect=/delete-account');
    }
  }, [user, authLoading, navigate]);

  const handleDelete = async () => {
    if (confirmText !== 'ELIMINAR') return;

    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke('delete-user-account');

      if (error) {
        throw error;
      }

      await supabase.auth.signOut();
      
      toast({
        title: 'Cuenta eliminada',
        description: 'Tu cuenta y todos tus datos han sido eliminados permanentemente.',
      });

      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      toast({
        title: 'Error',
        description: 'No se pudo eliminar tu cuenta. Por favor, intenta de nuevo.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setConfirmText('');
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">Cargando...</div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md mb-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </Button>
      </div>

      <Card className="w-full max-w-md border-destructive/20">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
            <AlertTriangle className="w-6 h-6 text-destructive" />
          </div>
          <CardTitle className="text-destructive text-2xl font-bold">
            Eliminar mi cuenta
          </CardTitle>
          <CardDescription className="text-gray-600 mt-2">
            Esta acción es <strong>permanente e irreversible</strong>. Se eliminarán:
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <ul className="list-disc list-inside text-sm text-gray-600 space-y-2 bg-gray-50 p-4 rounded-lg">
            <li>Tu perfil e información personal</li>
            <li>Tu historial de inscripciones y pagos</li>
            <li>Tus logros y estadísticas</li>
            <li>Todos los datos asociados a tu cuenta</li>
          </ul>

          <div className="space-y-2 pt-4">
            <Label htmlFor="confirmDelete" className="text-sm font-medium">
              Escribe <strong className="text-destructive">ELIMINAR</strong> para confirmar
            </Label>
            <Input
              id="confirmDelete"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
              placeholder="ELIMINAR"
              className="font-mono border-destructive/20 focus-visible:ring-destructive"
            />
          </div>
        </CardContent>

        <CardFooter className="flex flex-col gap-3">
          <Button
            onClick={handleDelete}
            disabled={confirmText !== 'ELIMINAR' || loading}
            variant="destructive"
            className="w-full font-semibold"
          >
            {loading ? 'Eliminando cuenta...' : 'Confirmar eliminación de cuenta'}
          </Button>
          <Button 
            variant="outline" 
            onClick={() => navigate(-1)} 
            className="w-full"
            disabled={loading}
          >
            Cancelar
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default DeleteAccountPage;
